/**
 * Cognito Authentication Service
 * Provides all authentication functions for the StepFlow AI application
 */

import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute,
  CognitoIdToken,
  CognitoAccessToken,
  CognitoRefreshToken,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';

let cognitoConfig = null;

/**
 * Initialize Cognito: prefer VITE_COGNITO_* (StepFlow IdP); else legacy Autosynth GENAPI POST.
 */
export const initializeCognitoConfig = async () => {
  const vitePool = import.meta.env.VITE_COGNITO_USER_POOL_ID;
  const viteClient = import.meta.env.VITE_COGNITO_CLIENT_ID;
  if (vitePool && viteClient) {
    cognitoConfig = {
      UserPoolId: vitePool,
      ClientId: viteClient,
      IdentityPoolId: import.meta.env.VITE_COGNITO_IDENTITY_POOL_ID || '',
      AuthApiEndpoint: import.meta.env.VITE_GENAPI_URL || '',
    };
    // VITE config is AUTHORITATIVE. A STALE ``ClientId`` left in localStorage by
    // a prior (legacy autosynth) session must NEVER survive — ``getUserPool``
    // falls back to localStorage when ``cognitoConfig`` isn't in memory yet, so
    // a disagreeing cached id would silently auth against the wrong pool
    // ("User does not exist" — incidents 2026-06-12 / -14). Overwrite it.
    const staleClient = localStorage.getItem('ClientId');
    if (staleClient && staleClient !== viteClient) {
      console.warn(`[cognito] overwriting stale ClientId ${staleClient} → ${viteClient}`);
    }
    localStorage.setItem('UserPoolId', cognitoConfig.UserPoolId);
    localStorage.setItem('ClientId', cognitoConfig.ClientId);
    localStorage.setItem('IdentityPoolId', cognitoConfig.IdentityPoolId || '');
    localStorage.setItem('AuthApiEndpoint', cognitoConfig.AuthApiEndpoint || '');
    return cognitoConfig;
  }

  try {
    const apiUrl = window.RUNTIME_CONFIG?.GENAPI_URL ||
                   import.meta.env.VITE_GENAPI_URL ||
                   'https://2bjr7ohm50.execute-api.us-east-1.amazonaws.com/dev/autosynth/v1/core';

    const response = await axios.post(apiUrl);
    const body = JSON.parse(response.data.body);

    cognitoConfig = {
      UserPoolId: body['/autosynth/userpool/id'],
      ClientId: body['/autosynth/userpool/client_id'],
      IdentityPoolId: body['/autosynth/identitypool/id'],
      AuthApiEndpoint: body['/autosynth/authapi/endpoint'],
    };

    localStorage.setItem('UserPoolId', cognitoConfig.UserPoolId);
    localStorage.setItem('ClientId', cognitoConfig.ClientId);
    localStorage.setItem('IdentityPoolId', cognitoConfig.IdentityPoolId);
    localStorage.setItem('AuthApiEndpoint', cognitoConfig.AuthApiEndpoint);

    return cognitoConfig;
  } catch (error) {
    console.error('Failed to initialize Cognito config:', error);
    throw error;
  }
};

/**
 * Get Cognito User Pool
 */
const getUserPool = () => {
  if (!cognitoConfig) {
    // VITE config is AUTHORITATIVE: if present, derive from it directly and
    // never trust a (possibly stale, legacy-autosynth) localStorage ClientId.
    // This is the same precedence as initializeCognitoConfig — repeated here
    // because getUserPool can run before init in some load orders.
    const vitePool = import.meta.env.VITE_COGNITO_USER_POOL_ID;
    const viteClient = import.meta.env.VITE_COGNITO_CLIENT_ID;
    if (vitePool && viteClient) {
      cognitoConfig = {
        UserPoolId: vitePool,
        ClientId: viteClient,
        IdentityPoolId: import.meta.env.VITE_COGNITO_IDENTITY_POOL_ID || '',
        AuthApiEndpoint: import.meta.env.VITE_GENAPI_URL || '',
      };
      localStorage.setItem('UserPoolId', vitePool);
      localStorage.setItem('ClientId', viteClient);
    } else {
      // No VITE config (legacy autosynth path) — fall back to localStorage.
      const UserPoolId = localStorage.getItem('UserPoolId');
      const ClientId = localStorage.getItem('ClientId');
      if (UserPoolId && ClientId) {
        cognitoConfig = {
          UserPoolId,
          ClientId,
          IdentityPoolId: localStorage.getItem('IdentityPoolId'),
          AuthApiEndpoint: localStorage.getItem('AuthApiEndpoint')
        };
      } else {
        throw new Error('Cognito configuration not initialized');
      }
    }
  }

  return new CognitoUserPool({
    UserPoolId: cognitoConfig.UserPoolId,
    ClientId: cognitoConfig.ClientId
  });
};

/**
 * Sign up a new user (pool uses email as username — same value for Cognito username + email attr).
 */
export const signUp = async (email, password, name) => {
  const userPool = getUserPool();
  const trimmedEmail = email.trim();
  const attributeList = [
    new CognitoUserAttribute({ Name: 'email', Value: trimmedEmail }),
    new CognitoUserAttribute({ Name: 'name', Value: name.trim() }),
  ];

  return new Promise((resolve, reject) => {
    userPool.signUp(trimmedEmail, password, attributeList, null, (err, result) => {
      if (err) {
        reject(err);
        return;
      }
      resolve(result.user);
    });
  });
};

/**
 * Confirm user registration with verification code
 */
export const confirmSignUp = async (username, code) => {
  const userPool = getUserPool();
  const userData = {
    Username: username,
    Pool: userPool
  };
  const cognitoUser = new CognitoUser(userData);
  
  return new Promise((resolve, reject) => {
    cognitoUser.confirmRegistration(code, true, (err, result) => {
      if (err) {
        reject(err);
        return;
      }
      resolve(result);
    });
  });
};

/**
 * Resend the email verification code for an UNCONFIRMED user.
 * Cognito sends a fresh code to the email-on-file. Useful when the original
 * code expired, the email landed in spam, or the user lost the page.
 */
export const resendSignUp = async (username) => {
  const userPool = getUserPool();
  const cognitoUser = new CognitoUser({
    Username: username,
    Pool: userPool,
  });

  return new Promise((resolve, reject) => {
    cognitoUser.resendConfirmationCode((err, result) => {
      if (err) {
        reject(err);
        return;
      }
      resolve(result);
    });
  });
};

/**
 * Hydrate Cognito user + localStorage after Hosted UI (OAuth) token response.
 */
export const establishSessionFromOAuthTokens = async ({
  idToken,
  accessToken,
  refreshToken,
}) => {
  const userPool = getUserPool();
  const decoded = jwtDecode(idToken);
  const username =
    decoded['cognito:username'] || decoded.email || decoded.sub;
  const session = new CognitoUserSession({
    IdToken: new CognitoIdToken({ IdToken: idToken }),
    AccessToken: new CognitoAccessToken({ AccessToken: accessToken }),
    RefreshToken: refreshToken
      ? new CognitoRefreshToken({ RefreshToken: refreshToken })
      : undefined,
  });
  const cognitoUser = new CognitoUser({
    Username: username,
    Pool: userPool,
  });
  cognitoUser.setSignInUserSession(session);
  localStorage.setItem('idToken', idToken);
  localStorage.setItem('accessToken', accessToken);
  if (refreshToken) {
    localStorage.setItem('refreshToken', refreshToken);
  }
  return { cognitoUser, username };
};

/**
 * Sign in user
 */
export const signIn = async (username, password) => {
  const userPool = getUserPool();
  const authenticationData = {
    Username: username,
    Password: password
  };
  const authenticationDetails = new AuthenticationDetails(authenticationData);
  
  const userData = {
    Username: username,
    Pool: userPool
  };
  const cognitoUser = new CognitoUser(userData);
  
  return new Promise((resolve, reject) => {
    cognitoUser.authenticateUser(authenticationDetails, {
      onSuccess: (result) => {
        const idToken = result.getIdToken().getJwtToken();
        const accessToken = result.getAccessToken().getJwtToken();
        const refreshToken = result.getRefreshToken().getToken();
        
        // Store tokens
        localStorage.setItem('idToken', idToken);
        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('refreshToken', refreshToken);
        
        resolve({
          idToken,
          accessToken,
          refreshToken,
          user: cognitoUser
        });
      },
      onFailure: (err) => {
        reject(err);
      }
    });
  });
};

/**
 * Get current authenticated user
 */
export const getCurrentUser = () => {
  const userPool = getUserPool();
  return userPool.getCurrentUser();
};

/**
 * Get current session
 */
export const getCurrentSession = () => {
  const cognitoUser = getCurrentUser();
  
  if (!cognitoUser) {
    return Promise.reject('No user logged in');
  }
  
  return new Promise((resolve, reject) => {
    cognitoUser.getSession((err, session) => {
      if (err) {
        reject(err);
        return;
      }
      resolve(session);
    });
  });
};

/**
 * Refresh authentication token
 */
export const refreshToken = () => {
  const cognitoUser = getCurrentUser();
  
  return new Promise((resolve, reject) => {
    if (!cognitoUser) {
      reject('No current user available for refreshing token');
      return;
    }
    
    cognitoUser.getSession((err, session) => {
      if (err) {
        reject(err);
        return;
      }
      
      const refreshTokenObj = session.getRefreshToken();
      cognitoUser.refreshSession(refreshTokenObj, (err, newSession) => {
        if (err) {
          reject(err);
          return;
        }
        
        const idToken = newSession.getIdToken().getJwtToken();
        const accessToken = newSession.getAccessToken().getJwtToken();
        const refreshToken = newSession.getRefreshToken().getToken();
        
        // Update stored tokens
        localStorage.setItem('idToken', idToken);
        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('refreshToken', refreshToken);
        
        resolve(newSession);
      });
    });
  });
};

/**
 * Sign out user
 */
export const signOut = () => {
  const cognitoUser = getCurrentUser();
  
  if (cognitoUser) {
    cognitoUser.signOut();
  }
  
  // Clear all stored tokens
  localStorage.removeItem('idToken');
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('cognitoToken');
  localStorage.removeItem('cognitoUser');
  localStorage.removeItem('cognitoName');
};

/**
 * Initiate forgot password flow
 */
export const forgotPassword = async (username) => {
  const userPool = getUserPool();
  const userData = {
    Username: username,
    Pool: userPool
  };
  const cognitoUser = new CognitoUser(userData);
  
  return new Promise((resolve, reject) => {
    cognitoUser.forgotPassword({
      onSuccess: (result) => resolve(result),
      onFailure: (err) => reject(err)
    });
  });
};

/**
 * Confirm new password with verification code
 */
export const confirmPassword = async (username, verificationCode, newPassword) => {
  const userPool = getUserPool();
  const userData = {
    Username: username,
    Pool: userPool
  };
  const cognitoUser = new CognitoUser(userData);
  
  return new Promise((resolve, reject) => {
    cognitoUser.confirmPassword(verificationCode, newPassword, {
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err)
    });
  });
};

/**
 * Get user attributes
 */
export const getUserAttributes = () => {
  const cognitoUser = getCurrentUser();
  
  if (!cognitoUser) {
    return Promise.reject('No user logged in');
  }
  
  return new Promise((resolve, reject) => {
    cognitoUser.getSession((err, _session) => {
      if (err) {
        reject(err);
        return;
      }
      
      cognitoUser.getUserAttributes((err, attributes) => {
        if (err) {
          reject(err);
          return;
        }
        
        const attributesMap = {};
        attributes.forEach(attr => {
          attributesMap[attr.Name] = attr.Value;
        });
        
        resolve(attributesMap);
      });
    });
  });
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = async () => {
  try {
    const session = await getCurrentSession();
    return session.isValid();
  } catch (_err) {
    return false;
  }
};
